import React from 'react';

import classNames from 'classnames';

import Count from '../count/count';
import {Icon} from '../icon/icon';

export interface IInternalLinkProps {
    className: string;
    children: React.ReactNode;
}

export type InternalLink = (props: IInternalLinkProps) => JSX.Element;

export interface INavigationItemProps {
    url: string | (() => void);
    label: string;
    count?: number;
    current?: boolean;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    icon?: string;
}

export interface INavigationProps {
    items: INavigationItemProps[];
    className?: string;
    direction?: 'auto' | 'vertical';
    onItemClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const NavigationItem = (props: INavigationItemProps): JSX.Element => {
    const className: string = classNames(
        'navigation__item',
        {
            'is-current': props.current,
        },
    );

    const content = (
        <React.Fragment>
            {props.icon &&
            <span className="navigation__icon-container">
                <Icon className="navigation__icon" name={props.icon}/>
                {props.count &&
                <Count
                    className="navigation__count"
                    number={props.count}
                    color="red"
                    size={'sm'}
                />
                }
            </span>}
            {props.label}
        </React.Fragment>
    );

    if (typeof props.url === 'string') {
        return (
            <li className={className}>
                <a href={props.url} className="navigation__link" onClick={props.onClick}>
                    {content}
                </a>
            </li>
        );
    } else {
        return (
            <li className={className}>
                <a href="#" className="navigation__link" onClick={(event): void => {
                    if (props.onClick) {
                        props.onClick(event);
                    }
                    (props.url as (() => void))();
                }}>
                    {content}
                </a>
            </li>
        );
    }
};

const Navigation = ({
    direction = 'auto',
    className,
    items,
    onItemClick,
}: INavigationProps): JSX.Element => {
    const computedClassName: string = classNames(
        'navigation',
        {
            [`navigation--direction-${direction}`]: direction,
        },
        className,
    );

    const handleItemClick = (event: React.MouseEvent<HTMLAnchorElement>, item: INavigationItemProps): void => {
        if (item.onClick) {
            item.onClick(event);
        }

        if (onItemClick) {
            onItemClick(event);
        }
    };

    const renderItems: () => JSX.Element[] = (): JSX.Element[] => items.map((item: INavigationItemProps, index: number) => (
        <NavigationItem
            key={index}
            onClick={(event: React.MouseEvent<HTMLAnchorElement>): void => handleItemClick(event, item)}
            {...item}
        />
    ));

    return (
        <nav className={computedClassName}>
            <ul className="navigation__list">
                {renderItems()}
            </ul>
        </nav>
    );
};

export default Navigation;
